<template>
  <div class="mt-4 mb-8 container" :style="cssVars">
    <v-breadcrumbs class="d-print-none">
      <v-breadcrumbs-item :exact="true" :to="{ name: 'home', params: {} }"
        >Home</v-breadcrumbs-item
      >
      <v-breadcrumbs-divider>/</v-breadcrumbs-divider>
      <v-breadcrumbs-item :to="{ name: 'genericQrPrint', params: {} }">
        Generic QR Code Print
      </v-breadcrumbs-item>
    </v-breadcrumbs>

    <div class="d-print-none">
      <v-textarea
        v-model="inputText"
        outlined
        label="Labels Input"
        hint="Each line: serial,label (quote fields if they contain commas, e.g., &quot;ABC&quot;,&quot;DEF,GHI&quot;)"
        rows="5"
      ></v-textarea>
      <div class="d-flex justify-center">
        <v-text-field class="mx-2 flex-grow-1" v-model="filterTerm" outlined dense label="Filter entries" hint="Example: 1, 2, 5 - 10" />
        <v-text-field class="mx-2 flex-grow-0" v-model.number="emptyPadding" outlined dense label="Empty Padding" type="number"/>
        
        <v-btn class="mx-2" @click="print()">Print</v-btn>
      </div>
      <v-expansion-panels class="mb-5">
        <v-expansion-panel>
          <v-expansion-panel-header>Advanced Print Settings</v-expansion-panel-header>
          <v-expansion-panel-content>
            <page-params
              pageName="genericQrPrint"
              vuexName="qrPrintParameters"
              :parameters="settings"
            />
            <v-divider />
            <div>
              <div class="d-flex">
                <v-text-field label="Brand Name" class="mx-2"
                  outlined
                  dense v-model="parameters.brandName"/>
                <v-btn class="ml-2" @click="addStickerField()">Add Field</v-btn>
              </div>
            </div>
            <div v-for="(field, index) in parameters.optionalFields" :key="index">
              <div class="d-flex">
                <v-text-field label="Field Label" class="mx-2"
                    outlined
                    dense v-model="field.label"/>
                <v-text-field label="Field Value" class="mx-2"
                    outlined
                    dense v-model="field.value"/>
                <v-btn @click="deleteField(index)" icon><v-icon>mdi-delete-outline</v-icon></v-btn>
              </div>
            </div>
            <v-radio-group row v-model="parameters.codeType" label="Code Type:">
              <v-radio label="QR" value="qr"></v-radio>
              <v-radio label="Barcode" value="barcode"></v-radio>
            </v-radio-group>
            <div class="d-flex">
              <v-text-field
                class="mx-2"
                suffix="px"
                outlined
                dense
                label="Left"
                v-model="parameters.left"
                type="number"
              ></v-text-field>
              <v-text-field
                class="mx-2"
                suffix="px"
                outlined
                dense
                label="Top"
                v-model="parameters.top"
                type="number"
              ></v-text-field>
              <v-text-field
                class="mx-2"
                suffix="px"
                outlined
                dense
                label="Right"
                v-model="parameters.right"
                type="number"
              ></v-text-field>
              <v-text-field
                class="mx-2"
                suffix="px"
                outlined
                dense
                label="Bottom"
                v-model="parameters.bottom"
                type="number"
              ></v-text-field>
            </div>
            <div class="d-flex">
              <v-text-field
                class="mx-2"
                suffix="pt"
                outlined
                dense
                label="Font Size"
                v-model="parameters.fontSize"
                type="number"
              ></v-text-field>
              <v-text-field
                class="mx-2"
                suffix="pt"
                outlined
                dense
                label="Header Font Size"
                v-model="parameters.headerFontSize"
                type="number"
              ></v-text-field>
              <v-text-field
                class="mx-2"
                suffix="pt"
                outlined
                dense
                label="Footer Font Size"
                v-model="parameters.footerFontSize"
                type="number"
              ></v-text-field>
              <v-text-field
                class="mx-2"
                suffix="pt"
                outlined
                dense
                :label="parameters.codeType === 'qr' ? 'QR Code Size' : 'Barcode Height'"
                v-model="parameters.qrSize"
                type="number"
              ></v-text-field>
              <v-text-field
                v-if="parameters.codeType === 'barcode'"
                class="mx-2"
                suffix="px"
                outlined
                dense
                label="Barcode Bar Width"
                v-model="parameters.barcodeBarWidth"
                type="number"
              ></v-text-field>
              <v-text-field
                class="mx-2"
                suffix="cols"
                outlined
                dense
                label="Number of Columns"
                v-model="parameters.cols"
                type="number"
              ></v-text-field>
            </div>
            <v-divider class="mb-5"/>
          </v-expansion-panel-content>
        </v-expansion-panel>
      </v-expansion-panels>
    </div>
    <div class="sticker-container" :style="{ '--cols': parameters.cols }">
      <div v-for="(rowEntries, rowIndex) in rows" :key="rowIndex" class="sticker-row">
        <div
          class="sticker"
          v-for="(entry, colIndex) in rowEntries"
          :key="entry.id"
          :style="{
            paddingTop: parameters.top + 'px',
            paddingRight: parameters.right + 'px',
            paddingBottom: parameters.bottom + 'px',
            paddingLeft: parameters.left + 'px',
            fontSize: parameters.fontSize + 'pt',
          }"
        >
          <div v-if="entry.empty">
            <div :style="`height: ${cellHeight}px`"></div>
          </div>
          <div v-if="!entry.empty">
            <div :class="companyName.startsWith('Bergsoe') ? 'bg-img':'panya-bg-img'"></div>
            <div ref="cellContent" class="cell-content">
              <div v-if="parameters.codeType === 'qr'">
                <div class="d-flex">
                  <div class="my-auto">
                    <qriously :value="getCodeValue(entry)" :size="Number(parameters.qrSize)" />
                  </div>
                  <div class="ml-3">
                    <table class="qr-info">
                      <tbody>
                      <tr>
                        <td colspan="2" :style="`font-size: ${parameters.headerFontSize}pt`" class="font-weight-bold">{{ companyName }}</td>
                      </tr>
                      <tr v-if="parameters.brandName">
                        <td class="font-weight-light text-caption">Brand</td>
                        <td>{{ parameters.brandName }}</td>
                      </tr>
                      <tr v-if="entry.serial">
                        <td class="font-weight-light text-caption">Serial</td>
                        <td>{{ entry.serial }}</td>
                      </tr>
                      <tr v-if="entry.label">
                        <td class="font-weight-light text-caption">Label</td>
                        <td>{{ entry.label }}</td>
                      </tr>
                      <tr v-for="(field, index) in parameters.optionalFields" :key="index">
                        <td class="font-weight-light text-caption">{{field.label}}</td>
                        <td>{{ field.value }}</td>
                      </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
              <div v-else>
                <div v-if="entry.label" class="text-center font-weight-bold" :style="`font-size: ${parameters.headerFontSize}pt`">{{ entry.label }}</div>
                <div class="text-center">
                  <vue-barcode tag="svg" :value="getCodeValue(entry)" :options="{ format: 'CODE128', height: Number(parameters.qrSize) / 2, width: Number(parameters.barcodeBarWidth), displayValue: false }" :key="parameters.barcodeBarWidth + '-' + parameters.qrSize" />
                </div>
              </div>
              <div
                class="ma-0 pa-0 font-weight-light footer-text"
                :style="`font-size: ${parameters.footerFontSize}pt;`"
                v-if="companyAddress"
              >
                {{ companyAddress }} Tel: {{ companyPhone }}
                <div class="text-caption font-weight-light font-size-small d-print-none" style="float: right; position: relative;">{{entryIndex(entry)}}</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
const PageParams = () => import("@/components/common/PageParams.vue");
const VueBarcode = () => import("vue-barcode");

export default {
  data() {
    return {
      companyName: process.env.VUE_APP_COMPANY_NAME,
      companyAddress: process.env.VUE_APP_COMPANY_ADDRESS,
      companyPhone: process.env.VUE_APP_COMPANY_PHONE,
      inputText: "",
      filterTerm: null,
      emptyPadding: 0,
      cellHeight: 100,
    };
  },
  components: {
    PageParams,
    VueBarcode
  },
  computed: {
    ...mapGetters("qrPrintParameters", ["parameters"]),

    cssVars() {
      return {
        '--padding-top': Number(this.parameters.top || 0) + 'px',
        '--padding-right': Number(this.parameters.right || 0) + 'px',
        '--padding-bottom': Number(this.parameters.bottom || 0) + 'px',
        '--padding-left': Number(this.parameters.left || 0) + 'px',
      }
    },
    settings() {
      return this.parameters;
    },
    parsedLabels() {
      return this.inputText.split('\n').map((line, index) => {
        const trimmedLine = line.trim();
        if (!trimmedLine) return null;

        // Simple CSV parser for two fields, handling quotes
        const parts = trimmedLine.match(/^(?:"([^"]*)"|([^,]+)),(?:"([^"]*)"|(.*))$/);
        if (parts) {
          const serial = (parts[1] || parts[2] || '').trim();
          const label = (parts[3] || parts[4] || '').trim();
          if (serial) {
            return { id: index, serial, label };
          }
        } else {
          // Fallback: split at first comma if no quotes match
          const match = trimmedLine.match(/([^,]*),(.*)/);
          const serial = match ? match[1].trim() : trimmedLine;
          const label = match ? match[2].trim() : '';
          if (serial) {
            return { id: index, serial, label };
          }
        }
        return null;
      }).filter(item => item);
    },
    entries() {
      return this.parsedLabels;
    },
    filteredEntries(){
      let entries = [];
      if(this.entries){
        if(this.filterTerm){
          const matchIndexes = this.parseFilterTerms(this.filterTerm);
          entries = this.entries.filter( (e, index) => {
            return matchIndexes.includes(index+1);
          })
        }
        else{ 
          entries = this.entries; 
        }
      }

      let nonEmptyEntries = entries.filter(e => !e.empty);
      if (nonEmptyEntries.length === 1) {
        entries.push({empty: true});
      }

      if(this.emptyPadding > 0 && entries.length > 0){
        let emptyCells = []
        for(var i=0; i<this.emptyPadding; i++){
          emptyCells.push({empty: true});
        }
        entries = emptyCells.concat(entries);
      }
      
      return entries;
    },
    rows() {
      const rows = [];
      const cols = Number(this.parameters.cols) || 1; // Ensure number
      for (let i = 0; i < this.filteredEntries.length; i += cols) {
        rows.push(this.filteredEntries.slice(i, i + cols));
      }
      return rows;
    }
  },
  methods: {
    ...mapActions("navigation", ["hideDrawer"]),
    ...mapActions("qrPrintParameters", ["updateParameters"]),

    async print() {
      await this.hideDrawer();
      window.print();
    },
    getCodeValue(entry) {
      return entry.serial;
    },
    entryIndex(entry){
      if(this.entries){
        const index = this.entries.findIndex(e => e.id === entry.id);
        if(index >= 0){
          return index + 1;
        }
        else{ return 0 }
      }
    },
    parseFilterTerms(terms){
      const entryIndexes = new Set();
      if(terms){
        const list = terms.split(',');
        list.forEach(l => {
          //check if range
          if(l.includes('-')){
            const range = l.split('-');
            if(range.length == 2){
              const start = parseInt(range[0].trim());
              const end = parseInt(range[1].trim());
              let cursor = start;
              while (cursor <= end) {
                entryIndexes.add(cursor);
                cursor++;
              }
            }
          }
          else{ 
            entryIndexes.add(parseInt(l.trim()));
          }
        })
      }
      return [...entryIndexes];
    },
    addStickerField(){
      let fields = this.parameters.optionalFields;
      if(!fields){
        fields = []
      }
      fields.push({label: `Field ${fields.length+1} Name`, value: ''});
      this.$set(this.parameters, 'optionalFields', fields);
    },
    deleteField(index){
      let fields = this.parameters.optionalFields;
      if(fields){
        fields.splice(index, 1);
        this.$set(this.parameters, 'optionalFields', fields);
      }
    }
  },
  created() {
    if (!this.parameters.codeType) {
      this.updateParameters({ codeType: 'barcode' });
    }
    if (!this.parameters.barcodeBarWidth) {
      this.updateParameters({ barcodeBarWidth: 2 });
    }
  },
  updated(){
     const cells = this.$refs.cellContent;
    if(cells && cells.length > 0){
      this.cellHeight = Math.max(...cells.map(cell => cell.clientHeight));
    }
  }
};
</script>
<style scoped>
table.qr-info > tbody > tr > td:nth-child(1){
  padding-right: 5px;
}

div.sticker {
  position: relative;
  border: 1px dashed black;
  min-height: 150px; /* Prevent shrinking for single items */
  min-width: 0; /* Prevent flex overflow */
  overflow: hidden; /* Prevent content overflow */
}

div.bg-img{
  position: absolute;
  top: calc(-1 * var(--padding-top));
  left: calc(-1 * var(--padding-left));
  right: calc(-1 * var(--padding-right));
  bottom: calc(-1 * var(--padding-bottom));
  opacity: .15;
  background-size: contain;
  background-repeat: no-repeat;
  background-image: url('~@/assets/logo.png');
  background-position: center;
}

div.panya-bg-img{
  position: absolute;
  top: calc(-1 * var(--padding-top));
  left: calc(-1 * var(--padding-left));
  right: calc(-1 * var(--padding-right));
  bottom: calc(-1 * var(--padding-bottom));
  opacity: .35;
  background-size: contain;
  background-repeat: no-repeat;
  background-image: url('~@/assets/panya-logo.png');
  background-position: center;
}

.sticker-container {
  display: block;
  width: 100%;
}

.sticker-row {
  display: flex;
  flex-wrap: nowrap;
  width: 100%;
}

.sticker {
  flex: 0 0 calc(100% / var(--cols));
  max-width: calc(100% / var(--cols));
  box-sizing: border-box;
}

.footer-text {
  white-space: normal;
  word-break: break-word;
}

@media print {

  @page {
    margin: 6mm 4mm 5mm 5mm;
    orientation: portrait;
  }

  .container {
    max-width: none !important;
    width: 100% !important;
    padding: 0 !important;
    margin: 0 !important;
  }

  .sticker-container {
    display: block !important;
    width: 100% !important;
  }

  .sticker-row {
    display: flex !important;
    flex-wrap: nowrap !important;
    width: 100% !important;
    page-break-inside: avoid;
  }

  .sticker {
    flex: 0 0 calc(100% / var(--cols)) !important;
    max-width: calc(100% / var(--cols)) !important;
    box-sizing: border-box;
    position: relative;
    page-break-inside: avoid;
    display: block !important;
    border: 0px;
    min-width: 0 !important;
    overflow: hidden !important;

    -webkit-print-color-adjust: exact !important;   /* Chrome, Safari, Edge */
    color-adjust: exact !important;                 /*Firefox*/
  }
}
</style>